import React from "react";
import { CalendarCheck2, Sun, Umbrella, BriefcaseBusiness } from "lucide-react";
import type { ILeaveBalanceReportItem } from "../types/leave.api.types";

export interface LeaveTypeMeta {
  key: "annual" | "sick" | "casual" | "comp_off";
  color: string;
  bg: string;
  icon: React.ReactNode;
  /** Regex tested against the master-config label */
  match: RegExp;
}

export const LEAVE_TYPE_META: LeaveTypeMeta[] = [
  {
    key: "annual",
    color: "#6D28D9",
    bg: "#EDE9FE",
    icon: React.createElement(CalendarCheck2, { className: "w-5 h-5", style: { color: "#6D28D9" } }),
    match: /annual/i,
  },
  {
    key: "sick",
    color: "#B45309",
    bg: "#FEF3C7",
    icon: React.createElement(Umbrella, { className: "w-5 h-5", style: { color: "#B45309" } }),
    match: /sick/i,
  },
  {
    key: "casual",
    color: "#059669",
    bg: "#D1FAE5",
    icon: React.createElement(BriefcaseBusiness, { className: "w-5 h-5", style: { color: "#059669" } }),
    match: /casual/i,
  },
  {
    key: "comp_off",
    color: "#2563EB",
    bg: "#DBEAFE",
    icon: React.createElement(Sun, { className: "w-5 h-5", style: { color: "#2563EB" } }),
    match: /comp\s*-?\s*off|compensatory/i,
  },
];

export function getLeaveTypeMeta(label?: string): LeaveTypeMeta | undefined {
  if (!label) return undefined;
  return LEAVE_TYPE_META.find((m) => m.match.test(label));
}

export function getLeaveValue(
  record: ILeaveBalanceReportItem,
  key: LeaveTypeMeta["key"],
): number {
  const meta = LEAVE_TYPE_META.find((m) => m.key === key);
  if (!meta) return 0;
  const entry = (record.leaveBalances ?? []).find((b) => meta.match.test(b.label));
  return entry?.value ?? 0;
}

export function getUtilization(
  val: number,
  label: string
): { total: number; used: number; percent: number; color: string; bg: string } {
  let defaultAllocation = 10;
  const l = label.toLowerCase();
  if (l.includes("annual")) defaultAllocation = Math.max(val, 18);
  else if (l.includes("sick")) defaultAllocation = Math.max(val, 12);
  else if (l.includes("casual")) defaultAllocation = Math.max(val, 6);
  else if (l.includes("comp")) defaultAllocation = Math.max(val, 4);
  else if (l.includes("maternity") || l.includes("paternity")) defaultAllocation = val;
  else if (l.includes("bereavement")) defaultAllocation = Math.max(val, 5);
  else defaultAllocation = Math.max(val, 10);

  const total = defaultAllocation;
  const used = Math.max(0, total - val);
  const percent = total > 0 ? Math.min(100, Math.round((used / total) * 100)) : 0;

  let color = "text-[var(--color-success)]";
  let bg = "bg-[var(--color-success)]";

  if (percent >= 80) {
    color = "text-[var(--color-error-dark)]";
    bg = "bg-[var(--color-error-dark)]";
  } else if (percent > 50) {
    color = "text-[var(--color-warning-dark)]";
    bg = "bg-[var(--color-warning-dark)]";
  } else if (percent > 25) {
    color = "text-[var(--color-success-dark)]";
    bg = "bg-[var(--color-success-dark)]";
  }

  return { total, used, percent, color, bg };
}

export function getAggregateUtilization(record: ILeaveBalanceReportItem) {
  let totalAllocated = 0;
  let totalRemaining = 0;
  for (const b of record.leaveBalances ?? []) {
    const val = b.value ?? 0;
    const { total } = getUtilization(val, b.label);
    totalAllocated += total;
    totalRemaining += val;
  }
  const used = Math.max(0, totalAllocated - totalRemaining);
  const percent = totalAllocated > 0 ? Math.min(100, Math.round((used / totalAllocated) * 100)) : 0;
  
  let color = "text-[var(--color-success)]";
  let bg = "bg-[var(--color-success)]";

  if (percent >= 80) {
    color = "text-[var(--color-error-dark)]";
    bg = "bg-[var(--color-error-dark)]";
  } else if (percent > 50) {
    color = "text-[var(--color-warning-dark)]";
    bg = "bg-[var(--color-warning-dark)]";
  } else if (percent > 25) {
    color = "text-[var(--color-success-dark)]";
    bg = "bg-[var(--color-success-dark)]";
  }

  return { totalAllocated, totalRemaining, used, percent, color, bg };
}

/**
 * Calculates number of leave days between two dates.
 * Considers half-day flag (0.5x).
 */
export function calculateLeaveDays(
  startDate: string,
  endDate: string,
  isHalfDay: boolean,
): number {
  if (!startDate || !endDate) return 0;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
  return isHalfDay ? diffDays * 0.5 : diffDays;
}

/**
 * Validates whether the selected date range contains any Sundays.
 */
export function checkDateSpansSunday(startDate: string, endDate: string): boolean {
  if (!startDate || !endDate) return false;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const cursor = new Date(start);
  while (cursor <= end) {
    if (cursor.getDay() === 0) return true;
    cursor.setDate(cursor.getDate() + 1);
  }
  return false;
}
